import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpContext,
  HttpContextToken,
  HttpResponse,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import {
  catchError,
  concatMap,
  EMPTY,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { JwtService } from './jwt.service';
import { BrowserStorageService } from './browser-storage.service';

const CHECK_TOKEN = new HttpContextToken<boolean>(() => true);
const REFRESH_TOKEN = new HttpContextToken<boolean>(() => false);

export const noCheckToken = () => {
  return new HttpContext().set(CHECK_TOKEN, false);
};

export const refreshTokenContext = () => {
  return new HttpContext().set(REFRESH_TOKEN, true);
};

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptorService implements HttpInterceptor {
  constructor(
    private router: Router,
    private authService: AuthService,
    private jwtService: JwtService,
    private browserStorageService: BrowserStorageService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const isRequestNeedToken = req.context.get(CHECK_TOKEN);
    if (!isRequestNeedToken) {
      return next.handle(req);
    }

    const isValidToken = this.jwtService.isValidToken();
    const isValidRefreshToken = this.jwtService.isValidRefreshToken();
    if (isValidToken) {
      // Token válido, sigue normalmente
      return this.addToken(req, next);
    }
    if (isValidRefreshToken) {
      // Token vencido pero refresh válido, refresca y reintenta
      return this.authService.refreshToken().pipe(
        tap((response) => {
          // Mapeo para compatibilidad con la respuesta del backend
          const token = response?.data?.access_token || response?.token;
          const refreshToken =
            response?.data?.refresh_token || response?.refresh_token;
          if (token) {
            this.browserStorageService.set('token', token);
          }
          if (refreshToken) {
            this.browserStorageService.set('refresh_token', refreshToken);
          }
        }),
        concatMap(() => this.addToken(req, next)),
      );
    }

    this.authService.logout();
    this.router.navigate(['/auth/login']);
    return EMPTY;
  }

  private handle2FAHeaders(headers: HttpHeaders): boolean {
    if (headers.get('x-2fa-setup-required') === 'true') {
      sessionStorage.setItem('2fa_setup_required', 'true');
      this.router.navigate(['/auth/login']);
      return true;
    }
    if (headers.get('X-2FA-Required') === 'true') {
      sessionStorage.setItem('2fa_required', 'true');
      this.authService.logout();
      this.router.navigate(['/auth/login']);
      return true;
    }
    return false;
  }

  private addToken(request: HttpRequest<any>, next: HttpHandler) {
    const token = this.browserStorageService.get('token');
    const outRequest = token
      ? request.clone({
          setHeaders: { authorization: `Bearer ${token}` },
          withCredentials: true,
        })
      : request;

    return next.handle(outRequest).pipe(
      switchMap((event) => {
        if (
          event instanceof HttpResponse &&
          this.handle2FAHeaders(event.headers)
        ) {
          return EMPTY;
        }
        return of(event);
      }),
      catchError((error: HttpErrorResponse) => {
        if (this.handle2FAHeaders(error.headers)) return EMPTY;
        return throwError(() => error);
      }),
    );
  }

  updateAccessToken(request: HttpRequest<any>, next: HttpHandler) {
    const isValidRefreshToken = this.jwtService.isValidRefreshToken();
    if (isValidRefreshToken) {
      return this.authService
        .refreshToken()
        .pipe(concatMap(() => this.addToken(request, next)));
    }
    this.authService.logout();
    this.router.navigate(['/auth/login']);
    return EMPTY;
  }
}
